import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import SuperAdminLayout from '../components/admin/SuperAdminLayout';
import { adminService } from '../services/api';

const SuperAdminClinics = () => {
    const [clinicData, setClinicData] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await adminService.getDashboardStats();
                if (response.data && response.data.success && response.data.data.subscriptions) {
                    const transformedClinics = response.data.data.subscriptions.map(sub => {
                        let statusText = 'Aktif';
                        let statusColor = 'bg-[#0DBC94]';
                        
                        if (sub.status === 'pending') {
                            statusText = 'Trial';
                            statusColor = 'bg-[#FFB536]';
                        } else if (sub.status === 'failed' || sub.status === 'expired') {
                            statusText = 'Kadaluarsa';
                            statusColor = 'bg-[#F64E60]';
                        }

                        return {
                            id: sub.order_id,
                            name: sub.clinic_name,
                            avatarColor: 'bg-gray-100',
                            address: sub.address || '-',
                            date: sub.created_at ? sub.created_at.split('T')[0] : '-',
                            time: '',
                            package: sub.plan || '-',
                            price: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(sub.amount || 0),
                            status: statusText,
                            statusColor: statusColor,
                            displayDate: sub.created_at ? new Date(sub.created_at).toLocaleString('id-ID', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute:'2-digit'}).replace(',', ' -') + ' PM' : '-'
                        };
                    });
                    setClinicData(transformedClinics);
                }
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
            }
        };
        fetchStats();
    }, []);

    const [selectedDate, setSelectedDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('Semua');

    const filteredData = clinicData.filter(item => {
        const matchDate = selectedDate ? item.date === selectedDate : true;
        const matchStatus = filterStatus === 'Semua' ? true : item.status === filterStatus;
        return matchDate && matchStatus;
    });
    const sortedData = [...filteredData].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        return String(a?.[sortConfig.key] ?? '').localeCompare(String(b?.[sortConfig.key] ?? ''), 'id', { numeric: true }) * direction;
    });
    const totalPages = Math.max(1, Math.ceil(sortedData.length / perPage));
    const paginatedData = sortedData.slice((currentPage - 1) * perPage, currentPage * perPage);

    const [isEditClinicOpen, setIsEditClinicOpen] = useState(false);
    const [currentClinic, setCurrentClinic] = useState(null);
    const [clinicFormData, setClinicFormData] = useState({ status: 'paid' });

    const handleEditClinicClick = (clinic) => {
        setCurrentClinic(clinic);
        let rawStatus = 'paid';
        if (clinic.status === 'Trial') rawStatus = 'pending';
        if (clinic.status === 'Kadaluarsa') rawStatus = 'failed';
        setClinicFormData({ status: rawStatus });
        setIsEditClinicOpen(true);
    };

    const handleUpdateClinic = async (e) => {
        e.preventDefault();
        try {
            await adminService.updateSubscription(currentClinic.id, clinicFormData);
            
            // Re-fetch or manually update local state
            const updatedClinics = clinicData.map(clinic => {
                if (clinic.id === currentClinic.id) {
                    let statusText = 'Aktif';
                    let statusColor = 'bg-[#0DBC94]';
                    if (clinicFormData.status === 'pending') {
                        statusText = 'Trial';
                        statusColor = 'bg-[#FFB536]';
                    } else if (clinicFormData.status === 'failed' || clinicFormData.status === 'expired') {
                        statusText = 'Kadaluarsa';
                        statusColor = 'bg-[#F64E60]';
                    }
                    return { ...clinic, status: statusText, statusColor: statusColor };
                }
                return clinic;
            });
            setClinicData(updatedClinics);
            setIsEditClinicOpen(false);
        } catch (error) {
            console.error("Failed to update clinic status", error);
        }
    };

    return (
        <SuperAdminLayout activePage="clinics" title="Klinik">
            {/* Total Klinik Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 mb-8 p-8">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Total Klinik</h3>

                    {/* Filters */}
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:border-[#0DBC94] transition-colors"
                        />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:border-[#0DBC94] transition-colors cursor-pointer"
                        >
                            <option value="Semua">Semua Status</option>
                            <option value="Aktif">Aktif</option>
                            <option value="Trial">Trial</option>
                            <option value="Kadaluarsa">Kadaluarsa</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => setSortConfig((p) => ({ key: 'name', direction: p.key === 'name' && p.direction === 'asc' ? 'desc' : 'asc' }))} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama Klinik</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Alamat</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tanggal - Waktu</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Paket</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Harga</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {paginatedData.length > 0 ? (
                                paginatedData.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 ${item.avatarColor} rounded-full flex-shrink-0`}></div>
                                                <span className="font-bold text-sm text-gray-700">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.address}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.displayDate}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.package}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.price}</td>
                                        <td className="px-6 py-5">
                                            <div className={`flex items-center justify-center w-[110px] h-8 ${item.statusColor} text-white text-[10px] font-black rounded-lg`}>
                                                {item.status}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <button
                                                onClick={() => handleEditClinicClick(item)}
                                                className="p-2 text-gray-400 hover:text-[#06B6D4] hover:bg-[#E0F7FA] rounded-lg transition-colors"
                                                title="Edit Status"
                                            >
                                                <Icon icon="material-symbols:edit-outline" className="text-xl" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                                        Tidak ada data yang ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{currentPage}/{totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>

            {/* Edit Clinic Status Modal */}
            {isEditClinicOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Edit Status Klinik</h3>
                            <button onClick={() => setIsEditClinicOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Icon icon="material-symbols:close" className="text-2xl" />
                            </button>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm font-medium text-gray-500">Nama Klinik: <span className="font-bold text-gray-800">{currentClinic?.name}</span></p>
                        </div>
                        <form onSubmit={handleUpdateClinic} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                                <select
                                    value={clinicFormData.status}
                                    onChange={(e) => setClinicFormData({ ...clinicFormData, status: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#0DBC94] focus:ring-1 focus:ring-[#0DBC94] transition-all"
                                >
                                    <option value="paid">Aktif</option>
                                    <option value="pending">Trial</option>
                                    <option value="failed">Kadaluarsa</option>
                                </select>
                            </div>
                            <button type="submit" className="w-full py-3 bg-[#0DBC94] text-white font-bold rounded-xl hover:bg-[#0ba783] transition-colors mt-4">
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </SuperAdminLayout>
    );
};

export default SuperAdminClinics;
